import React, { useState, useEffect, useMemo } from 'react'
import { Link, Redirect, useLocation, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import classNames from 'classnames'
import styled from 'styled-components'
import startCase from 'lodash/startCase'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner'

import { Heading, Group, ProgressBar, HydraTable } from '@peracto/peracto-ui'
import { GET_ONE, GET_LIST, useClient } from '@peracto/client'

const POLL_INTERVAL = 5000
const FAILED_AFTER_HOURS = 2

const STATUSES = {
    copied: { label: 'Copied', className: 'badge-success' },
    skipped_exists: { label: 'Already Exists', className: 'badge-info' },
}

const ImportSummaryPod = styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px;
    border: 1px solid #ddd;
    width: 100%;
    border-radius: 3px;
    font-size: 16px;
`

const ImportSummary = ({ summaryTitle, total, className = '' }) => (
    <ImportSummaryPod className={className}>
        <span className="!font-bold">{summaryTitle}</span>
        <span>{total}</span>
    </ImportSummaryPod>
)

const StatusBadge = ({ line }) => {
    if (!line.successful) {
        return <span className="badge badge-pill badge-danger">Failed</span>
    }

    const status = STATUSES[line.data?.status] || {
        label: startCase(line.data?.status || 'done'),
        className: 'badge-secondary',
    }

    return <span className={`badge badge-pill ${status.className}`}>{status.label}</span>
}

const ContentAssetImportStatus = () => {
    const { client } = useClient()
    const { id } = useParams()
    const type = new URLSearchParams(useLocation().search).get('type') || 'page'
    const backUrl = `/${type}s`

    const [summary, setSummary] = useState()
    const [loading, setLoading] = useState(true)
    const [redirect, setRedirect] = useState()

    const isComplete = summary && summary.processedCount >= summary.totalCount
    const hoursSinceCreated = summary
        ? (Date.now() - new Date(summary.createdAt).getTime()) / (1000 * 60 * 60)
        : 0
    const isFailed = summary && !isComplete && hoursSinceCreated >= FAILED_AFTER_HOURS

    const fetchStatus = async () => {
        try {
            const { data: importSummary, response } = await client(GET_ONE, 'imports', {
                id: `/imports/${id}`,
            })

            if (response?.status === 404) {
                setRedirect(backUrl)
                return
            }

            setSummary(importSummary)
        } catch (e) {
            console.error(e)
            setRedirect(backUrl)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchStatus()
    }, [])

    useEffect(() => {
        if (!summary || isComplete || isFailed) {
            return
        }

        const timeout = setTimeout(fetchStatus, POLL_INTERVAL)

        return () => clearTimeout(timeout)
    }, [summary])

    const columns = useMemo(
        () => [
            {
                Header: 'Asset',
                accessor: 'raw',
                id: 'asset',
                Cell: ({ cell: { value } }) => (
                    <span className="overflow-anywhere">{value?.key || '-'}</span>
                ),
            },
            {
                Header: 'Status',
                id: 'status',
                Cell: ({ row }) => <StatusBadge line={row.original} />,
            },
            {
                Header: 'Error',
                accessor: 'errors',
                id: 'error',
                Cell: ({ cell: { value } }) => (
                    <span className="overflow-anywhere">
                        {value?.length > 0 ? value[0].message : '-'}
                    </span>
                ),
            },
        ],
        []
    )

    const onFetchData = options => client(GET_LIST, 'import-lines', options)

    return (
        <>
            {redirect && <Redirect to={redirect} />}
            <Helmet>
                <title>Content Asset Copy | Peracto</title>
            </Helmet>

            <div className="form-container">
                <Heading name="Content Asset Copy">
                    <div className="!flex !items-center !ml-2">
                        <Link className="btn btn-outline-primary" to={backUrl}>
                            <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                            Back to {startCase(type)}s
                        </Link>
                    </div>
                </Heading>

                <Group key="import-summary" id="import-summary" name="Summary">
                    {loading || !summary ? (
                        'Loading data...'
                    ) : (
                        <>
                            {isFailed && (
                                <div className="alert alert-danger">
                                    This asset copy failed to complete. Please import the content
                                    again.
                                </div>
                            )}
                            {!isFailed && !isComplete && (
                                <div className="!flex !justify-end !items-center !my-1">
                                    <FontAwesomeIcon icon={faSpinner} spin className="!mr-2" />
                                    <span>
                                        {summary.processedCount === 0
                                            ? 'Waiting for the copy to start'
                                            : 'Copying assets'}
                                    </span>
                                </div>
                            )}
                            {!isFailed && (
                                <ProgressBar
                                    total={summary.totalCount}
                                    current={summary.processedCount}
                                />
                            )}
                            <div className="row">
                                <div className="col-12 col-md-6 col-lg-3">
                                    <ImportSummary
                                        summaryTitle="Assets to Copy"
                                        total={summary.totalCount}
                                    />
                                </div>
                                <div className="col-12 col-md-6 col-lg-3">
                                    <ImportSummary
                                        summaryTitle="Processed"
                                        total={summary.processedCount}
                                    />
                                </div>
                                <div className="col-12 col-md-6 col-lg-3">
                                    <ImportSummary
                                        summaryTitle="Successful"
                                        total={summary.successfulCount}
                                    />
                                </div>
                                <div className="col-12 col-md-6 col-lg-3">
                                    <ImportSummary
                                        summaryTitle="Errors"
                                        total={summary.errorCount}
                                        className={classNames({
                                            '!text-danger': summary.errorCount > 0,
                                        })}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </Group>

                <Group key="import-lines" id="import-lines" name="Assets">
                    {loading ? (
                        'Loading data...'
                    ) : (
                        <HydraTable
                            key={isComplete ? 'complete' : 'copying'}
                            columns={columns}
                            onFetchData={onFetchData}
                            alwaysFilter={{ import: id }}
                            noResultsMessage="No assets processed yet"
                        />
                    )}
                </Group>
            </div>
        </>
    )
}

export default ContentAssetImportStatus
